import React, { useState } from "react";
import { TimePicker } from "@/components/AntdMomentPicker";
import { Card, Switch, Typography, Checkbox, Alert, Spin, message, InputNumber } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
    enabledOrderMerge,
    OrderAutoMergeRule,
    saveAutoMergeBySameProduct,
    saveAutoMergeByTimeRange,
    saveAutoMergePresaleWithPresaleOnly,
    ShopAdvancedSettingDto,
    updateMaxAutoMergeOrderCount,
    updateOrderAutoMergeRule,
} from "@/services/api/shop";
import moment, { Moment } from "moment";
import { useRequest } from "ahooks";
import { useGlobalStore } from "@/store/hooks";
import ShopSettingDTO from "@/dto/shop/ShopSettingDTO";
import Authority from "@/enum/Authority";
import useImmediateMergeOrderOnEnable from "../hooks/useImmediateMergeOrderOnEnable";
import { observer } from "mobx-react-lite";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import AutoMergeExcludeProductSetting from "./AutoMergeExclusionRule";
import ShopType from "@/enum/ShopType";
import { isConsigneeMergeSupported } from "@/utils/ShopUtils";
import { PopoverText } from "@/components";

interface AutoMergeSettingProps {
    shopSetting: ShopSettingDTO;
    shopAdvancedSetting: ShopAdvancedSettingDto;
}
const initialAutoMergeTimeRange = moment().hour(18).minute(0).second(0);

function formatAutoMergeTimeRange(autoMergeTimeRange: string) {
    if (!autoMergeTimeRange) return initialAutoMergeTimeRange;

    const list = autoMergeTimeRange.split(":");

    if (list.length !== 3) return initialAutoMergeTimeRange;

    const [hour, minute, second] = list.map(Number);

    const autoMergeMoment = moment().hours(hour).minutes(minute).seconds(second);

    return autoMergeMoment;
}

const AutoMergeSetting: React.FC<AutoMergeSettingProps> = function (props) {
    const { shopSetting, shopAdvancedSetting } = props;
    const { id: shopId } = shopSetting;
    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();
    const { shopSettingMapping, shopAdvancedSettingStore } = globalStore;
    const [enableOrderAutoMerge, updateEnableOrderAutoMerge] = useState(shopSetting.enableOrderAutoMerge);
    const [enabledAutoMergeByTimeRange, updateEnabledAutoMergeByTimeRange] = useState(
        shopAdvancedSetting.enabledAutoMergeByTimeRange,
    );
    const [autoMergeTimeRange, updateAutoMergeTimeRange] = useState(() => {
        return formatAutoMergeTimeRange(shopAdvancedSetting.autoMergeTimeRange);
    });
    const [enabledAutoMergeBySameProduct, updateEnabledAutoMergeBySameProduct] = useState(
        shopAdvancedSetting.enabledAutoMergeBySameProduct,
    );
    const autoMergeOrderMaxCountLimit = shopSetting.type === ShopType.DOUDIAN ? 200 : 50;
    const [maxAutoMergeOrderCount, setMaxAutoMergeOrderCount] = useState(
        shopAdvancedSetting.maxAutoMergeOrderCount || autoMergeOrderMaxCountLimit,
    );
    const [sameConSigneeMerge, updateSameConSigneeMerge] = useState(() => {
        if (!shopAdvancedSetting.orderAutoMergeRule) return false;

        return shopAdvancedSetting.orderAutoMergeRule === OrderAutoMergeRule.SameConsignee;
    });

    const [enabledAutoMergePresaleWithPresaleOnly, updateEnabledAutoMergePresaleWithPresaleOnly] = useState(
        !!shopAdvancedSetting.enabledAutoMergePresaleWithPresaleOnly,
    );

    const { runAsync: saveShopAutoMerge, loading: loading1 } = useRequest(
        async (enabled) => {
            await enabledOrderMerge(enabled, shopId);

            shopSettingMapping.set(shopId, { ...shopSetting, enableOrderAutoMerge: enabled });

            updateEnableOrderAutoMerge(enabled);
        },
        { manual: true },
    );

    const { runAsync: saveShopAutoMergeByTimeRange, loading: loading2 } = useRequest(
        async (enabled: boolean, nextAutoMergeTimeRange: Moment) => {
            const autoMergeTimeRange = nextAutoMergeTimeRange?.format("HH:mm:ss");

            const setting = await saveAutoMergeByTimeRange(shopId, enabled, autoMergeTimeRange);

            shopAdvancedSettingStore.setAdvancedSetting(shopId, setting);

            updateEnabledAutoMergeByTimeRange(enabled);
            updateAutoMergeTimeRange(nextAutoMergeTimeRange);
        },
        {
            manual: true,
        },
    );

    const { runAsync: saveShopAutoMergeBySameProduct, loading: loading3 } = useRequest(
        async (enabled: boolean) => {
            const setting = await saveAutoMergeBySameProduct(shopId, enabled);

            shopAdvancedSettingStore.setAdvancedSetting(shopId, setting);

            updateEnabledAutoMergeBySameProduct(enabled);
        },
        {
            manual: true,
        },
    );

    const { runAsync: saveShopAutoMergeMaxOrderCount, loading: loading4 } = useRequest(
        async (maxAutoMergeOrderCount: number) => {
            await updateMaxAutoMergeOrderCount(shopId, maxAutoMergeOrderCount);

            shopAdvancedSettingStore.setAdvancedSetting(shopId, {
                ...shopAdvancedSetting,
                maxAutoMergeOrderCount,
            });

            setMaxAutoMergeOrderCount(maxAutoMergeOrderCount);
        },
        { manual: true },
    );

    const { runAsync: saveOrderAutoMergeRule, loading: loading5 } = useRequest(
        async (orderAutoMergeRule: OrderAutoMergeRule) => {
            await updateOrderAutoMergeRule(shopId, orderAutoMergeRule);

            shopAdvancedSettingStore.setAdvancedSetting(shopId, {
                ...shopAdvancedSetting,
                orderAutoMergeRule,
            });
        },
        { manual: true },
    );

    const { runAsync: saveShopAutoMergePresaleWithPresaleOnly, loading: loading6 } = useRequest(
        async (enabled: boolean) => {
            const setting = await saveAutoMergePresaleWithPresaleOnly(shopId, enabled);

            shopAdvancedSettingStore.setAdvancedSetting(shopId, setting);

            updateEnabledAutoMergePresaleWithPresaleOnly(enabled);
        },
        { manual: true },
    );

    const { handleImmediateMergeOrderOnEnable } = useImmediateMergeOrderOnEnable(shopId, () =>
        orderManagementStore.fetchOrders(shopId),
    );

    async function handleAutoMergeTimeRangeChange(nextAutoMergeTimeRange: moment.Moment) {
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        /** 修改合并时间段必须先开启自动合并订单 */
        const needEnabledAutoMerge = !enableOrderAutoMerge;
        if (needEnabledAutoMerge) {
            await saveShopAutoMerge(true);
        }

        await saveShopAutoMergeByTimeRange(true, nextAutoMergeTimeRange);

        message.success("保存成功");
    }

    async function handleEnableAutoMergeByTime(e) {
        const enabled = e.target.checked;
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        /** 开启按时间合并必须开启自动合并订单 */
        const needEnabledAutoMerge = enabled && !enableOrderAutoMerge;
        if (needEnabledAutoMerge) {
            await saveShopAutoMerge(true);
        }

        await saveShopAutoMergeByTimeRange(enabled, autoMergeTimeRange);

        message.success("保存成功");

        if (enabled) {
            handleImmediateMergeOrderOnEnable();
        }
    }

    async function handleEnabledAutoMergeBySameProduct(e) {
        const enabled = e.target.checked;
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        /** 开启按商品合并必须开启自动合并订单 */
        const needEnabledAutoMerge = enabled && !enableOrderAutoMerge;
        if (needEnabledAutoMerge) {
            await saveShopAutoMerge(true);
        }

        await saveShopAutoMergeBySameProduct(enabled);

        message.success("保存成功");

        if (enabled) {
            handleImmediateMergeOrderOnEnable();
        }
    }

    async function handleSaveShopAutoMerge(enabled) {
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        await saveShopAutoMerge(enabled);

        /** 关闭自动合并订单必须关闭其他有影响的选项 */
        if (!enabled) {
            const shopAdvanceSetting = await shopAdvancedSettingStore.mapping.get(shopId);

            if (shopAdvanceSetting.enabledAutoMergeByTimeRange) {
                await saveShopAutoMergeByTimeRange(false, autoMergeTimeRange || initialAutoMergeTimeRange);
            }

            if (shopAdvanceSetting.enabledAutoMergeBySameProduct) {
                await saveShopAutoMergeBySameProduct(false);
            }

            if (shopAdvanceSetting.enabledAutoMergePresaleWithPresaleOnly) {
                await saveShopAutoMergePresaleWithPresaleOnly(false);
            }
        } else {
            handleImmediateMergeOrderOnEnable();
        }

        message.success("保存成功");
    }

    async function handleMaxAutoMergeOrderCountChange() {
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        await saveShopAutoMergeMaxOrderCount(maxAutoMergeOrderCount);

        message.success("保存成功");
    }

    async function handleUpdateOrderMergeRule(e) {
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        const checked = e.target.checked;

        const orderAutoMergeRule = checked
            ? OrderAutoMergeRule.SameConsignee
            : OrderAutoMergeRule.SameBuyerAndConsignee;

        await saveOrderAutoMergeRule(orderAutoMergeRule);

        updateSameConSigneeMerge(checked);

        orderManagementStore.fetchOrders(shopId, orderManagementStore.tab);

        message.success("保存成功");
    }

    async function handleUpdateAutoMergePresaleWithPresaleOnly(e) {
        if (!globalStore.checkUserAuthority(Authority.ROLE_SHOP_MANAGEMENT)) {
            return;
        }

        const checked = e.target.checked;

        /** 开启预售订单自动合并规则必须开启自动合并订单 */
        const needEnabledAutoMerge = checked && !enableOrderAutoMerge;
        if (needEnabledAutoMerge) {
            await saveShopAutoMerge(true);
        }

        await saveShopAutoMergePresaleWithPresaleOnly(checked);

        message.success("保存成功");

        if (checked) {
            handleImmediateMergeOrderOnEnable();
        }
    }

    const loading = loading1 || loading2 || loading3 || loading4 || loading5 || loading6;

    return (
        <Spin spinning={loading}>
            <Card
                title={
                    <>
                        <Typography.Title level={5}>自动合并</Typography.Title>
                        <Typography.Paragraph type="secondary" className="small">
                            适用于店铺下所有商品都可合并打印；
                            <span className="text-warn fb">
                                订单有相同买家和地址且符合下列条件自动合并（部分平台支持买家不一致合并）
                            </span>
                        </Typography.Paragraph>

                        <Typography.Paragraph type="secondary" className="small">
                            在列表合并展示，支持筛选和合并打印发货单 支持设置更详细的自动合并规则
                        </Typography.Paragraph>
                    </>
                }
                extra={<Switch checked={enableOrderAutoMerge} onChange={handleSaveShopAutoMerge} />}
            >
                <Typography.Title level={5}>合并规则</Typography.Title>
                {/* 规则说明 */}
                <div
                    style={{
                        background: "#fafafa",
                        padding: 12,
                        borderRadius: 4,
                        marginBottom: 24,
                    }}
                >
                    <span className="text-warn">
                        自动合并规则是在相同地址的基础上增加限制，合并规则对自动合并和手动合并订单都生效
                    </span>

                    <Typography.Paragraph>
                        {/* 商品规则 */}
                        <div style={{ margin: "12px 0" }}>
                            <Typography.Title level={5} style={{ fontSize: "15px" }}>
                                商品相关规则:
                            </Typography.Title>

                            <Typography.Paragraph>
                                <AutoMergeExcludeProductSetting shopSetting={shopSetting} />
                            </Typography.Paragraph>

                            <Typography.Paragraph>
                                <Checkbox
                                    checked={enabledAutoMergeBySameProduct}
                                    onChange={handleEnabledAutoMergeBySameProduct}
                                >
                                    <PopoverText content="示例:同一个买家下单了3个订单，订单1有商品666-规格1、订单2有商品666-规格2，订单3有商品666-规格1，则订单1和订单3合并，订单2不进行合并">
                                        相同商品且相同规格ID的订单自动合并
                                    </PopoverText>
                                </Checkbox>
                            </Typography.Paragraph>
                        </div>

                        {/* 时间规则 */}
                        <div style={{ margin: "12px 0" }}>
                            <Typography.Title level={5} style={{ fontSize: "15px" }}>
                                订单时间规则:
                            </Typography.Title>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Checkbox
                                    checked={enabledAutoMergeByTimeRange}
                                    onChange={handleEnableAutoMergeByTime}
                                ></Checkbox>
                                <Typography.Text>仅自动合并每日</Typography.Text>
                                <TimePicker
                                    value={autoMergeTimeRange}
                                    onChange={handleAutoMergeTimeRangeChange}
                                    disabled={!enabledAutoMergeByTimeRange}
                                    format="HH:mm:ss"
                                    showNow={false}
                                    style={{ width: 120 }}
                                    suffixIcon={null}
                                    allowClear={false}
                                />
                                <Typography.Text>前的订单</Typography.Text>
                            </div>
                        </div>

                        {/* 订单合并数量规则 */}
                        <div style={{ margin: "12px 0" }}>
                            <Typography.Title level={5} style={{ fontSize: "15px" }}>
                                订单合并规则:
                            </Typography.Title>
                            {isConsigneeMergeSupported(shopSetting.type) ? (
                                <Typography.Paragraph>
                                    <Checkbox checked={sameConSigneeMerge} onChange={handleUpdateOrderMergeRule}>
                                        买家不一致的订单支持合并
                                    </Checkbox>
                                </Typography.Paragraph>
                            ) : null}

                            <Typography.Paragraph>
                                <Checkbox
                                    checked={enabledAutoMergePresaleWithPresaleOnly}
                                    onChange={handleUpdateAutoMergePresaleWithPresaleOnly}
                                >
                                    <PopoverText content="预售订单包括预售标签的订单和全款预售标签的订单">
                                        预售仅和预售订单自动合并
                                    </PopoverText>
                                </Checkbox>
                            </Typography.Paragraph>

                            <Typography.Paragraph>
                                <span style={{ marginLeft: "25px" }}>自动合并订单上限数量</span>

                                <InputNumber
                                    size="small"
                                    value={maxAutoMergeOrderCount}
                                    min={2}
                                    max={autoMergeOrderMaxCountLimit}
                                    step={1}
                                    precision={0}
                                    className="ml10"
                                    onChange={(value) => setMaxAutoMergeOrderCount(value)}
                                    onBlur={handleMaxAutoMergeOrderCountChange}
                                    style={{ width: "65px" }}
                                />
                            </Typography.Paragraph>
                        </div>

                        {/* 底部备注 */}
                        <Alert
                            title={
                                <span style={{ color: "#666" }}>
                                    <InfoCircleOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                                    订单重量、仓库等规则陆续开发，有需求可联系客服反馈
                                </span>
                            }
                            type="info"
                            showIcon={false}
                            style={{
                                marginTop: 32,
                                background: "#e6f7ff",
                                border: "1px solid #91d5ff",
                                borderRadius: 4,
                            }}
                        />
                    </Typography.Paragraph>
                </div>
            </Card>
        </Spin>
    );
};

export default observer(AutoMergeSetting);
